import { useSeo } from "../hooks/useSeo";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHead, Btn, Figure } from "../components/Bits";
import { IMAGES } from "../lib/images";

const VALUES = ["Performance", "Protection", "Durability", "Professionalism", "Simplicity"];

const PARAS = [
  "Combat sport is growing quickly, but many facilities still have limited access to professional surface options, clear product advice and modern installation solutions.",
  "MAKO was created to change that.",
  "We focus exclusively on the surface of the training environment: puzzle mats, tatami tiles, rollout mats and protective wall padding.",
  "By keeping the brand specialised, MAKO can help gyms and academies choose a system built around their room, discipline, usage and long-term plans.",
  "MAKO exists to make professional combat spaces more accessible, better presented and better equipped for the demands of serious training.",
];

export default function About() {
  useSeo({
    title: "About MAKO Mats | Combat Surface Specialists",
    description:
      "MAKO Mats is a specialist combat-surface brand supplying professional mats and protective wall padding for modern training facilities.",
  });

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About MAKO"
        title="Built for the future"
        subtitle="of combat sport"
        copy="A specialist brand focused entirely on the surface of the training environment."
        image={IMAGES.athletes}
      />

      <section className="bg-white py-16 lg:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 lg:grid-cols-12 lg:gap-20 lg:px-10">
          <div className="lg:col-span-7">
            {PARAS.map((p, i) => (
              <Reveal key={p} delay={i * 0.05}>
                <p
                  className={`mb-7 leading-relaxed text-[#3B3B3B] ${
                    i === 1
                      ? "font-display text-xl font-bold uppercase tracking-tight text-[#111111] sm:text-2xl"
                      : "text-sm sm:text-base"
                  }`}
                >
                  {p}
                </p>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <div className="mt-10 border-t border-[#111111]/10 pt-10">
                <p className="mako-label text-[#7A7F85]">Brand promise</p>
                <p className="mt-5 font-display text-2xl font-bold uppercase leading-tight tracking-tight text-[#111111] sm:text-3xl">
                  A better surface for better training.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <Figure
                src={IMAGES.mat_texture}
                alt="Close-up of MAKO mat surface texture"
                ratio="aspect-[4/5]"
                caption="Surface detail — tatami texture"
              />
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 border border-[#111111]/10 bg-[#F5F5F2] p-8">
                <p className="mako-label text-[#0F4C81]">Brand values</p>
                <ul className="mt-6 space-y-3">
                  {VALUES.map((v) => (
                    <li
                      key={v}
                      className="border-l-2 border-[#0F4C81] pl-4 font-display text-sm font-semibold uppercase tracking-wide text-[#111111]"
                    >
                      {v}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative bg-[#111111] py-20 lg:py-32">
        <div className="mako-grid-bg-dark absolute inset-0 opacity-60" aria-hidden="true" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHead
            tone="light"
            eyebrow="Specialist by design"
            title="One focus: the surface"
            copy="MAKO stands independently as a combat-surface brand. Mats, wall padding and the planning that connects them — nothing else."
          />
          <div className="mt-12">
            <Btn to="/contact" variant="white" data-testid="about-cta">
              Talk to MAKO
            </Btn>
          </div>
        </div>
      </section>
    </div>
  );
}
